import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import SchoolClassForm, { SchoolClass } from '../components/SchoolClassForm';
import TableFilter from '../components/TableFilter';
import ActionButtons from '../components/ActionButtons';
import ReportModal from '../components/ReportModal';

const SchoolClassesPage: React.FC = () => {
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [filteredClasses, setFilteredClasses] = useState<SchoolClass[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingClass, setEditingClass] = useState<SchoolClass | null>(null);
  const [reportModal, setReportModal] = useState<{ isOpen: boolean; isRow: boolean; id?: number }>({ isOpen: false, isRow: false });

  const fetchClasses = useCallback(async () => {
    try {
      const response = await api.get('classes/');
      setClasses(response.data);
      setFilteredClasses(response.data);
    } catch (error) {
      console.error("Error fetching classes", error);
    }
  }, []);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  const handleFilter = (filters: { [key: string]: string }) => {
    let result = classes;

    if (filters.name) {
      result = result.filter(c => c.name.toLowerCase().includes(filters.name.toLowerCase()));
    }

    setFilteredClasses(result);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setEditingClass(null);
    fetchClasses();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingClass(null);
  };

  const handleEdit = (schoolClass: SchoolClass) => {
    setEditingClass(schoolClass);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta turma?')) {
      try {
        await api.delete(`classes/${id}/`);
        fetchClasses();
      } catch (error) {
        console.error("Error deleting class", error);
        alert('Erro ao excluir turma.');
      }
    }
  };

  const openGeneralReports = () => setReportModal({ isOpen: true, isRow: false });
  const openRowReport = (id: number) => setReportModal({ isOpen: true, id, isRow: true });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1>Turmas</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Gerencie as turmas e anos escolares</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={openGeneralReports}>Relatórios de Turmas</button>
          {!showForm && (
            <button className="btn" onClick={() => setShowForm(true)}>+ Nova Turma</button>
          )}
        </div>
      </div>

      {showForm ? (
        <div style={{ marginBottom: '30px' }}>
          <SchoolClassForm
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            classToEdit={editingClass}
          />
        </div>
      ) : (
        <TableFilter
          filters={[
            { key: 'name', label: 'Nome da Turma' }
          ]}
          onFilter={handleFilter}
        />
      )}

      {reportModal.isOpen && (
        <ReportModal
          isOpen={reportModal.isOpen}
          onClose={() => setReportModal({ ...reportModal, isOpen: false })}
          category="School Class"
          rowId={reportModal.id}
          isRowReport={reportModal.isRow}
        />
      )}

      <div className="card" style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Nome da Turma</th>
              <th>Responsável</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredClasses.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', padding: '30px', color: 'var(--text-light)' }}>
                  Nenhuma turma encontrada.
                </td>
              </tr>
            ) : (
              filteredClasses.map((cls) => (
                <tr key={cls.id}>
                  <td style={{ fontWeight: '500' }}>{cls.name}</td>
                  <td>{cls.responsible_name || '-'}</td>
                  <td>
                    {cls.is_active ? (
                      <span className="badge badge-success">Ativa</span>
                    ) : (
                      <span className="badge badge-danger">Inativa</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <ActionButtons
                      onReport={() => cls.id && openRowReport(cls.id)}
                      onEdit={() => handleEdit(cls)}
                      onDelete={() => cls.id && handleDelete(cls.id)}
                      deletePermission="delete_schoolclass"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SchoolClassesPage;
